import { EChartsOption } from "echarts";
import { Box, Container } from "@mui/material";
import { RopChart } from "../../../../main/components/RopChart";
import { colorList } from "./GeneratedEnergyCharts";
import { EmptyTable } from "../../../clients/components/EmptyState";
import { ParetoDaily, ParetoDeOcorrenciasNoMes } from "../../types";
import { ParetoChartValues } from "../../../dashboard/components/ParetoChart";
import { parseTimeSpanToSeconds, secondsToTimeSpanString } from "../../../../main/utils/utils";

export function ocorrenciaChartDataFromAlarms(
  data: ParetoDeOcorrenciasNoMes | ParetoDaily,
  tipoFaixa?: "mes" | "dia" | undefined
): {
  tipoFaixa: "mes" | "dia";
  alarms: ParetoChartValues[];
  totalAmount: number;
} {
  if (tipoFaixa) {
    return {
      tipoFaixa: tipoFaixa,
      alarms: data.alarms,
      totalAmount: data.totalAmount,
    };
  }
  if ("type" in data) {
    return {
      tipoFaixa: "mes",
      alarms: data.alarms,
      totalAmount: data.totalAmount,
    };
  }
  return {
    tipoFaixa: "dia",
    alarms: data.alarms,
    totalAmount: data.totalAmount,
  };
}

export default function ParetoReportChart(props: {
  data: ParetoDeOcorrenciasNoMes | ParetoDaily;
  title?: string;
  tipoFaixa?: "mes" | "dia" | undefined
  timeDistribuition?: boolean
}) {
  const { data, title, tipoFaixa, timeDistribuition } = props;
  const ocorrenciaChartData = ocorrenciaChartDataFromAlarms(data, tipoFaixa);

  const eventosQntdColor = colorList?.[0] ?? "#6FA95A";
  const porcentagemColor = colorList?.[1] ?? "#557CAB";

  const percentageValues = ocorrenciaChartData.alarms.map((item, index) => {
    const previousValues = ocorrenciaChartData.alarms.slice(0, index);
    const previousPercentageSum = previousValues.reduce(
      (acc, item) => acc + item.percentage,
      0
    );
    return (item.percentage + previousPercentageSum).toFixed(2);
  });

  const alarmValues = ocorrenciaChartData.alarms.map((item) => timeDistribuition ? parseTimeSpanToSeconds(item.duration ?? "") : item.amount);
  const xLabels = ocorrenciaChartData.alarms.map((item) => item.code);
  const legendData = [(timeDistribuition ? "Tempo total por tracker" : "Quantidade de alarmes"), "Porcentagem"];

  function getTitleText(tipoFaixa: "mes" | "dia", title: string | undefined) {
    if (title) return title;
    else if (tipoFaixa == "mes") return "Pareto de ocorrências no mês";
    return "Pareto de ocorrências no dia";
  }

  const options: EChartsOption = {
    title: {
      text: getTitleText(ocorrenciaChartData.tipoFaixa, title),
      textStyle: {
        fontSize: 12,
      },
    },
    backgroundColor: "transparent",
    tooltip: {
      trigger: "axis",
      formatter: function (params: any) {
        let tooltip = `${params[0].axisValue}<br/>`;

        params.forEach((item: any) => {
          let value = item.data;
          if (value === '-' || value == null) {
            tooltip += `${item.marker} ${item.seriesName}: -<br/>`;
            return;
          }

          if (item.seriesName === 'Porcentagem') {
            tooltip += `${item.marker} ${item.seriesName}: ${value}%<br/>`;
          } else {
            tooltip += `${item.marker} ${item.seriesName}: ${secondsToTimeSpanString(value)}<br/>`;
          }
        });

        return tooltip;
      },
      axisPointer: {
        type: "cross",
        crossStyle: {
          color: "#999",
        },
      },
      confine: true,
    },

    legend: {
      data: legendData,
      left: "right",
    },
    xAxis: [
      {
        type: "category",
        data: xLabels,
        axisLabel: {
          fontSize: 10,
          rotate: 45,
        },
        axisPointer: {
          type: "shadow",
        },
      },
    ],
    yAxis: [
      {
        type: "value",
        name: timeDistribuition ? "Tempo" : "QTD",
        axisLabel: {
          formatter: function (value) {
            if (!timeDistribuition) return `${value}`;
            return secondsToTimeSpanString(value);
          },
        }
      },
      {
        type: "value",
        name: "Acumulado",
        axisLabel: {
          formatter: "{value} %",
        },
      },
    ],
    series: [
      {
        name: timeDistribuition ? "Tempo total por tracker" : "Quantidade de alarmes",
        type: "bar",
        tooltip: {
          valueFormatter: function (value) {
            let vPtBr: string;
            if (typeof value !== "number") {
              vPtBr = `${value}`;
            } else {
              vPtBr = value.toLocaleString("pt-BR", {
                minimumFractionDigits: 0,
                maximumFractionDigits: 0,
              });
            }
            return `${vPtBr}`;
          },
        },
        data: alarmValues,
        itemStyle: {
          color: eventosQntdColor,
        },
      },
      {
        name: "Porcentagem",
        yAxisIndex: 1,
        type: "line",
        tooltip: {
          valueFormatter: function (value) {
            let vPtBr: string;
            if (typeof value !== "number") {
              vPtBr = `${value}`;
            } else {
              vPtBr = value.toLocaleString("pt-BR", {
                minimumFractionDigits: 4,
                maximumFractionDigits: 4,
              });
            }
            return `${vPtBr} %`;
          },
        },
        data: percentageValues,
        itemStyle: {
          color: porcentagemColor,
        },
      },
    ],
  };

  const hasData =
    (alarmValues?.length ?? 0 > 0) || (percentageValues?.length ?? 0 > 0);

  return (
    <>
      {hasData ? (
        <Container
          sx={{
            display: "flex",
            flexDirection: "column",
            height: "90%",
            width: "100%",
          }}
        >
          <RopChart option={options} />
        </Container>
      ) : (
        <Box
          sx={{
            width: "100%",
            height: "100%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <EmptyTable title="Nenhum dado encontrado!" />
        </Box>
      )}
    </>
  );
}
